<template>
	<main>
		<div class="container">
			<section class="section_title">
				<div class="title_left">
					<div class="left_main">
						<img v-if="getEventDetailData" :src="getEventDetailData.event.image" alt="logo" loading="lazy" />
						<h3>{{ getEventDetailData?.event.event_name }}</h3>
					</div>
					<div class="left_sub">
						<p>{{ getEventDetailData?.event.ticker }}</p>
						<p>{{ getEventDetailData && changeMarketName(getEventDetailData.event.market) }}</p>
						<p>
							{{
								getEventDetailData &&
								getEventDetailData.event.base_date &&
								removeTimeFromDate(getEventDetailData.event.base_date)
							}}
						</p>
					</div>
				</div>
				<button class="title_right" @click="onClickBookMark">
					<IconStar :icon-color="isMarked ? '#FFB800' : '#D9D9D9'" />
				</button>
			</section>
			<StickyTabBar :items="tabItems" :auth="checkAuthData?.success" />
			<div class="content">
				<ReportOverviewJPN
					v-if="(route.query.tab === '개요' || !route.query.tab) && getInvestReportOverviewData"
					:data="getInvestReportOverviewData"
					:date="getEventDetailData?.event.base_date"
					:auth="checkAuthData?.success"
					:audio-file="getEventDetailData?.event.audio_file_path"
				/>
				<ReportScore v-if="route.query.tab === '점수' && getInvestReportScoreData" :data="getInvestReportScoreData" />
				<ReportFinance v-if="route.query.tab === '재무제표'" />
				<ReportBusinessJPN
					v-if="route.query.tab === '주요사업' && getInvestReportBusinessData"
					:data="getInvestReportBusinessData"
				/>
			</div>
		</div>
	</main>
</template>

<script setup lang="ts">
// core
import { computed, onMounted, ref, watch } from 'vue';
import { LocationQueryValue, useRoute } from 'vue-router';

import { useGetCheckAuthApi } from '@/api/auth';
import { useAdjustBookmarksApi } from '@/api/Interest';
// api
import {
	useGetEventDetailApi_JPN, // 종목 정보 불러오기
	useGetInvestReportBusinessApi_JPN, // '주요사업' 탭
	useGetInvestReportOverviewApi_JPN, // '개요' 탭
	useGetInvestReportScoreApi_JPN, // '점수' 탭
} from '@/api/report';
import IconStar from '@/components/atom/icon/IconStar.vue';
// component
import StickyTabBar from '@/components/atom/tabBar/StickyTabBar.vue';
import ReportBusinessJPN from '@/components/report/jpn/ReportBusinessJPN.vue';
import ReportOverviewJPN from '@/components/report/jpn/ReportOverviewJPN.vue';
import ReportFinance from '@/components/report/ReportFinance.vue';
import ReportScore from '@/components/report/ReportScore.vue';
// constant
import { COMPANY_TITLE, MESSAGE_CANCEL_COMPLETED, UNFAVORITE_PROMPT } from '@/constants/title';
// store
import { usePopupStore } from '@/stores/popup';
import { useToastStore } from '@/stores/toast';
// util
import { removeTimeFromDate } from '@/utils/function/format';

// -----variable-----
const popupStore = usePopupStore();
const toastStore = useToastStore();
const route = useRoute();

const isMarked = ref();

const queryPk = computed(() => String(route.params.eventPk));

const tabItems = ['개요', '점수', '재무제표', '주요사업'];
// -----api-----
const { data: checkAuthData, refetch: checkAuthRefetch } = useGetCheckAuthApi(
	computed(() => {
		return {
			num: 'ALL',
			student: false,
		};
	})
);
const { data: getEventDetailData, refetch: refetchGetEventDetail } = useGetEventDetailApi_JPN(
	computed(() => {
		return {
			event_pk: queryPk.value,
			master_pk: String(route.params.masterPk) ?? '',
		};
	})
);
const { data: getInvestReportOverviewData, refetch: refetchGetInvestReportOverview } =
	useGetInvestReportOverviewApi_JPN(
		computed(() => {
			return {
				event_pk: queryPk.value,
				master_pk: String(route.params.masterPk) ?? '',
			};
		})
	);
const { data: getInvestReportScoreData, refetch: refetchGetInvestReportScore } = useGetInvestReportScoreApi_JPN(
	computed(() => {
		return {
			event_pk: queryPk.value,
		};
	})
);
const { data: getInvestReportBusinessData, refetch: refetchGetInvestReportBusiness } =
	useGetInvestReportBusinessApi_JPN(
		computed(() => {
			return {
				event_pk: queryPk.value,
			};
		})
	);
const { mutateAsync: adjustBookmarks } = useAdjustBookmarksApi();
// -----function-----
const onClickBookMark = async () => {
	const params = [
		{
			event_pk: Number(route.params.eventPk),
			master_pk: null,
		},
	];

	if (isMarked.value) {
		popupStore.openPopup({
			content: COMPANY_TITLE,
			subtext: UNFAVORITE_PROMPT,
			confirm: async () => {
				const res = await adjustBookmarks({
					bookmarks: params,
				});
				if (res.data.success) {
					isMarked.value = false;
					toastStore.openToast({
						subContent: COMPANY_TITLE,
						content: MESSAGE_CANCEL_COMPLETED,
					});
				}
			},
		});
	} else {
		const res = await adjustBookmarks({
			bookmarks: params,
		});
		if (res.data.success) {
			isMarked.value = res.data.data.bookmark;
		}
	}
};
// 마켓 이름 변경 함수
const changeMarketName = (market: string) => {
	switch (market) {
		case 'tse':
			return '도쿄증권거래소';
	}
};

// -----composition-----
onMounted(async () => {
	await checkAuthRefetch();
	await refetchGetEventDetail();
});

watch(getEventDetailData, () => {
	isMarked.value = Boolean(getEventDetailData.value?.event.bookmark);
});

watch(
	() => route.query.tab,
	async (updatedTab: LocationQueryValue | LocationQueryValue[]) => {
		if (!updatedTab || updatedTab === '개요') {
			await refetchGetInvestReportOverview();
		} else if (updatedTab === '점수') {
			await refetchGetInvestReportScore();
		} else if (updatedTab === '주요사업') {
			await refetchGetInvestReportBusiness();
		}
		// 이 외의 탭은 하위 컴포넌트에서 호출
	},
	{ immediate: true }
);

// 해당 컴포넌트 '개요'탭 내에서 검색을 통해 이동했을 경우 마운트도 안되고 route.query.tab도 변경되지 않아서 해당 로직 추가
watch(
	() => queryPk.value,
	async () => {
		await refetchGetEventDetail();
		await refetchGetInvestReportOverview();
	}
);
</script>

<style scoped lang="scss">
.container {
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 68rem;
	padding-top: var(--header-height);
	margin: 7.2rem auto 8.8rem;
	@media screen and (max-width: 1280px) {
		margin: 4rem auto 8rem;
	}

	.section_title {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 2.4rem;
		@media screen and (max-width: 1280px) {
			padding-left: 2rem;
			padding-right: 2rem;
		}
		.title_left {
			display: flex;
			flex-direction: column;
			gap: 0.8rem;
			.left_main {
				display: flex;
				align-items: center;
				gap: 0.8rem;
				img {
					display: flex;
					width: 3.2rem;
					height: 3.2rem;
					justify-content: center;
					align-items: center;
					gap: 1rem;
					border-radius: 999px;
				}
				h3 {
					font-size: var(--font-s-x-large);
					font-weight: var(--font-w-bold);
					line-height: normal;
				}
			}
			.left_sub {
				display: flex;
				align-items: center;
				gap: 1.6rem;
				p {
					position: relative;
					color: var(--color-base-light, #c6c6d6);
					font-size: var(--font-s-small);
					font-weight: var(--font-w-light);
					line-height: normal;
				}
			}
			.left_sub p:not(:last-child)::after {
				content: '';
				position: absolute;
				top: 50%;
				right: -0.8rem;
				height: 50%;
				transform: translateY(-50%);
				width: 0.5px;
				background-color: var(--color-base-light, #c6c6d6);
			}
		}
	}
	.content {
		display: flex;
		margin-top: 3.2rem;
		@media screen and (max-width: 1280px) {
			padding-left: 2rem;
			padding-right: 2rem;
		}
		.wrap {
			display: flex;
			flex-direction: column;
			gap: 4.8rem;
			width: 100%;
		}
	}
}
</style>
